import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoIosCloseCircleOutline } from "react-icons/io";

import { Button } from "./Button";
import { twMerge } from "tailwind-merge";
import { createPortal } from "react-dom";

const CLOSE_DURATION_MS = 200;

export interface IConfirmModal {
  onConfirm?: () => any; // If return boolean then the modal will turn off
  onCancel?: Function;
  onOpen?: Function;
  title?: React.ReactNode;
  description?: any;
  children?: any;
  okText?: string;
  cancelText?: string;
  className?: string;
  okButtonProps?: any;
  cancelButtonProps?: any;
  style?: any;
  visible?: boolean;
  disabled?: boolean;
  showActions?: boolean;
}

export function ConfirmModal({
  onConfirm,
  onCancel,
  onOpen,
  title = "Confirmation!",
  description = "Do you really want to confirm?",
  children,
  okText,
  cancelText,
  okButtonProps,
  cancelButtonProps,
  visible: _visible,
  disabled,
  className = "",
  style,
  showActions = true,
}: IConfirmModal) {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [loading, setLoading] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  };

  const closeModal = useCallback(
    (callOnCancel = true) => {
      clearCloseTimer();
      setExiting(true);
      setEntered(false);
      closeTimerRef.current = setTimeout(() => {
        setVisible(false);
        setMounted(false);
        setExiting(false);
        if (callOnCancel && onCancel) onCancel();
      }, CLOSE_DURATION_MS);
    },
    [onCancel],
  );

  useEffect(() => {
    if (_visible !== undefined) {
      if (_visible) {
        clearCloseTimer();
        setVisible(true);
        setMounted(true);
        setExiting(false);
      } else if (mounted) {
        closeModal(false);
      } else {
        setVisible(false);
      }
    }
  }, [_visible, mounted, closeModal]);

  useEffect(() => {
    if (!mounted || exiting) {
      setEntered(false);
      return;
    }

    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [mounted, exiting]);

  useEffect(() => {
    if (!mounted) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted]);

  useEffect(() => () => clearCloseTimer(), []);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (onConfirm) {
        await onConfirm();
        closeModal(true);
      }
    } catch (error) {
      // Keep modal open on rejected confirmation handlers.
    }
    setLoading(false);
  };

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    clearCloseTimer();
    setVisible(true);
    setMounted(true);
    setExiting(false);
    if (onOpen) onOpen(e);
  };

  const modalContent = mounted && (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 sm:p-6"
      id="confirm-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={twMerge(
          "fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ease-out",
          entered && !exiting ? "opacity-100" : "opacity-0",
        )}
      />

      <div
        className={twMerge(
          // Avoid transform (scale) — it creates a containing block that makes CKEditor
          // treat the selection as clipped and park the link balloon off-screen.
          "relative z-10 flex w-full max-w-[95vw] flex-col overflow-hidden rounded-2xl border border-gray-100/80 bg-white shadow-2xl transition-opacity duration-200 ease-out md:w-60/100 lg:w-40/100",
          "max-h-[85vh]",
          entered && !exiting ? "opacity-100" : "opacity-0",
          className,
        )}
        style={style}
      >
        <div className="relative shrink-0 border-b border-gray-100 bg-gray-50/80 px-6 py-5">
          <button
            type="button"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-200/80 hover:text-gray-600"
            onClick={() => closeModal(true)}
            disabled={loading}
            aria-label="Close"
          >
            <IoIosCloseCircleOutline size={22} />
          </button>
          <div className="modal-head pr-10 text-center text-xl font-semibold leading-snug tracking-tight text-gray-900">
            {title}
          </div>
        </div>

        <div className="modal-sub min-h-0 flex-1 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-gray-600">
          {typeof description === "function" ? description({ onClose: () => closeModal(true) }) : description}
        </div>

        {showActions && (
          <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
            <Button
              text={cancelText || "Cancel"}
              className="border border-gray-300 bg-white px-5 hover:bg-gray-50 disabled:bg-gray-100 disabled:hover:bg-gray-100"
              textColor="text-gray-700"
              onClick={() => closeModal(true)}
              disabled={loading}
              {...cancelButtonProps}
            />
            <Button
              text={okText || "Confirm"}
              className="px-5 hover:bg-cyan-700 disabled:bg-cyan-600 disabled:opacity-90 disabled:hover:bg-cyan-600"
              bg="bg-cyan-600"
              textColor="text-white"
              loading={loading}
              loaderColor="#ffffff"
              onClick={handleConfirm}
              {...okButtonProps}
            />
          </div>
        )}
      </div>
    </div>
  );

  return disabled ? (
    children
  ) : (
    <>
      {children && React.cloneElement(children, { onClick })}
      {createPortal(modalContent, document.body)}
    </>
  );
}
